import { theme } from "@bokio/designsystem/theme/theme";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { exhaustiveCheck, safeExhaustiveSwitch } from "@bokio/utils/safeExhaustiveSwitch";
import { trackTrace } from "@bokio/utils/t";

import type { DiscriminatedUnionToIntersection } from "@bokio/mobile-web-shared/core/utils/typeUtils";

type BoundingBox = m.Classes.AI.BoundingBox;
type FieldsPrediction = m.Classes.AI.ReceiptFieldsPrediction;

export type OcrMatchName = "date" | "paymentDate" | "amount" | "paymentReference";

export interface ZoomState {
	scale: number;
	positionX: number;
	positionY: number;
}

export interface Point {
	x: number;
	y: number;
}

interface OcrBoxStroke {
	stroke: string;
	strokeOpacity: number;
}

interface OcrBoxColor extends OcrBoxStroke {
	fillOpacity: number;
	fill: string;
}

export interface OcrBox {
	topLeft: Point;
	topRight: Point;
	bottomRight: Point;
	bottomLeft: Point;
	color: OcrBoxColor;
}

export interface OcrLine {
	d: string;
	visible: boolean;
	stroke: string;
}

interface OcrMatch {
	line?: OcrLine;
	box: OcrBox;
	name: OcrMatchName;
	value: string;
}

type Colors = {
	[key in OcrMatchName | "default"]: OcrBoxStroke;
};

export const ocrColors: Colors = {
	// define highlight colors for OCR
	date: { stroke: theme.color.success[500], strokeOpacity: 0.8 },
	paymentDate: { stroke: theme.color.primary[500], strokeOpacity: 0.8 },
	amount: { stroke: theme.color.warning[500], strokeOpacity: 0.8 },
	paymentReference: { stroke: theme.color.destructive[200], strokeOpacity: 0.8 },
	default: { stroke: theme.color.ui[7], strokeOpacity: 0.8 },
};

type Match = m.Classes.AI.DateMatch | m.Classes.AI.NumericMatch | m.Classes.AI.TextMatch;

export interface SelectedPrediction {
	index: number;
	value: string;
	/**
	 * Set to true if the prediction is set by picking the best match
	 * False of the user has selected the prediction by clicking or by entering this value
	 * before e.g. in Uploads page.
	 */
	isDefault: boolean;
}

export type SelectedPredictions = {
	[key in OcrMatchName]: SelectedPrediction | undefined;
};

/**
 * Tells if the prediction should be set to the form input or not. This is
 * based on if the predictions is a default value or not. E.g. if it's a default value
 * selected automatically by us it shouldn't overwrite an existing input value.
 * @param predictionIsDefault the prediction
 * @param formValue the value of the form input that we want to set
 * @returns true if we should set the form input value
 */
export const shouldSetPredictionValue = (
	predictionIsDefault: boolean,
	formValue: string | m.Day | number | undefined,
) => !predictionIsDefault || (predictionIsDefault && !formValue);

export const bestMatch = <T>(matches: T[] | undefined) => (matches?.length ? matches[0] : undefined);

export const distance = (p1: Point, p2: Point) => {
	const a = p1.x - p2.x;
	const b = p1.y - p2.y;
	return Math.sqrt(a ** 2 + b ** 2);
};

export const rotatePoint = (theta: number, p: Point, origin: Point): Point => {
	const px = Math.cos(theta) * (p.x - origin.x) - Math.sin(theta) * (p.y - origin.y) + origin.x;
	const py = Math.sin(theta) * (p.x - origin.x) + Math.cos(theta) * (p.y - origin.y) + origin.y;
	return { x: px, y: py };
};

export const offsetPoint = (offsetLeft: number, p: Point, positionAdjustment: number): Point => {
	return {
		x: p.x + offsetLeft - positionAdjustment,
		y: p.y + positionAdjustment,
	};
};

export const calculateAdjustments = (
	orientation: number,
	center: Point,
	orientationAdjustment: number,
): [Point, number] => {
	const delta = 0.01;
	if (orientation < Math.PI * 0.5 - delta) {
		// Upside is up
		return [center, 0];
	} else if (orientation < Math.PI - delta) {
		// Upside is right
		return [{ x: center.y, y: center.x }, orientationAdjustment];
	} else if (orientation < Math.PI * 1.5 - delta) {
		// Upside is down
		return [center, 0];
	} else {
		// Upside is left
		return [{ x: center.y, y: center.x }, orientationAdjustment];
	}
};

export const boxToPolygonString = (b: OcrBox) => {
	return `${b.topLeft.x},${b.topLeft.y} ${b.topRight.x},${b.topRight.y} ${b.bottomRight.x},${b.bottomRight.y} ${b.bottomLeft.x},${b.bottomLeft.y}`;
};

interface GenerateBoxParams {
	scale: number;
	zoomState: ZoomState;
	offsetLeft: number;
	boundingBox: BoundingBox;
	color: OcrBoxColor;
	rotation: number;
	center: Point;
	orientation: number;
	orientationAdjustment: number;
}

export const generateBox = ({
	scale,
	zoomState,
	offsetLeft,
	boundingBox,
	color,
	rotation,
	center,
	orientation,
	orientationAdjustment,
}: GenerateBoxParams) => {
	const boxScale = scale * zoomState.scale;
	const padding = 5;
	const x = boundingBox.Left * boxScale - padding + zoomState.positionX;
	const y = boundingBox.Top * boxScale - padding + zoomState.positionY;
	const w = boundingBox.Width * boxScale + padding * 2;
	const h = boundingBox.Height * boxScale + padding * 2;

	const op1 = { x: x, y: y };
	const op2 = { x: x + w, y: y };
	const op3 = { x: x + w, y: y + h };
	const op4 = { x: x, y: y + h };

	const [adjustedCenter, positionAdjustment] = calculateAdjustments(orientation, center, orientationAdjustment);
	const p1 = rotatePoint(orientation + rotation, op1, adjustedCenter);
	const p2 = rotatePoint(orientation + rotation, op2, adjustedCenter);
	const p3 = rotatePoint(orientation + rotation, op3, adjustedCenter);
	const p4 = rotatePoint(orientation + rotation, op4, adjustedCenter);

	const res: OcrBox = {
		topLeft: offsetPoint(offsetLeft, p1, positionAdjustment),
		topRight: offsetPoint(offsetLeft, p2, positionAdjustment),
		bottomRight: offsetPoint(offsetLeft, p3, positionAdjustment),
		bottomLeft: offsetPoint(offsetLeft, p4, positionAdjustment),
		color,
	};

	return res;
};

const generateLineFromBoundingBoxes = (sourceBox: DOMRect, targetBox: DOMRect, box: OcrBox, color: string): OcrLine => {
	const pos = sourceBox;
	const x1 = 0;
	const y1 = pos.top + pos.height / 2 - targetBox.top;
	const fieldPos: Point = { x: x1, y: y1 };

	const boxPoints = [box.topLeft, box.topRight, box.bottomRight, box.bottomLeft];
	boxPoints.sort((a, b) => distance(a, fieldPos) - distance(b, fieldPos));
	const x2 = (boxPoints[0].x + boxPoints[1].x) / 2;
	const y2 = (boxPoints[0].y + boxPoints[1].y) / 2;

	const cx = (x2 - x1) / 3 + x1;
	const cy = y1;
	const ex = ((x2 - x1) * 2) / 3 + x1;
	const ey = y2;

	const line: OcrLine = {
		stroke: color,
		d: `M${x1},${y1}C${cx},${cy} ${ex},${ey} ${x2},${y2}`,
		visible: x1 >= 0 && y1 >= 0 && x2 >= 0 && y2 >= 0, // Hide line when it is outside viewport
	};

	return line;
};

export const generateLine = (
	input: HTMLInputElement,
	image: HTMLImageElement,
	box: OcrBox,
	color: string,
	setShadowOnInput: boolean,
): OcrLine | undefined => {
	if (setShadowOnInput) {
		input.style.boxShadow = `0 0 0 2px ${color}`;
	}

	return generateLineFromBoundingBoxes(input.getBoundingClientRect(), image.getBoundingClientRect(), box, color);
};

export type GetMatchesParams = (
	| { multiPrediction: true; showType: OcrMatchName | undefined }
	| { multiPrediction: false; hideTypes: OcrMatchName[] }
) & {
	multiPrediction: boolean;
	scale: number;
	zoomState: ZoomState;
	offsetLeft: number;
	prediction: FieldsPrediction;
	selectedPredictions: SelectedPredictions;
	image: HTMLImageElement;
	inputGetter: (field: OcrMatchName) => HTMLInputElement | undefined;
};

export const getMatches = (props: GetMatchesParams): { fieldMatches: OcrMatch[]; hideFields: OcrMatchName[] } => {
	const {
		image,
		scale,
		zoomState,
		offsetLeft,
		inputGetter,
		multiPrediction,
		selectedPredictions,
		prediction,
		hideTypes = [],
		showType = undefined,
	} = props as unknown as DiscriminatedUnionToIntersection<GetMatchesParams, "multiPrediction"> & {
		multiPrediction: boolean;
	};

	const imageRect = image.getBoundingClientRect();
	const orientationAdjustment = (imageRect.height - imageRect.width) / 2;
	const center = { x: imageRect.width / 2, y: imageRect.height / 2 };
	const rotation = prediction.Rotation;
	const orientation = prediction.Orientation;

	const getMatchFromPossibleMatch = (element: Match, field: OcrMatchName, color: OcrBoxColor, showLine: boolean) => {
		const box = generateBox({
			scale: scale,
			zoomState: zoomState,
			offsetLeft,
			boundingBox: element.BoundingBox,
			color,
			rotation,
			center,
			orientation,
			orientationAdjustment,
		});

		const input = inputGetter(field);

		const lineResult =
			input && image && showLine && zoomState.scale === 1
				? generateLine(input, image, box, color.stroke, !multiPrediction)
				: undefined;
		if (input && image && box) {
			return {
				box,
				line: lineResult,
				name: field,
				value: element.Value instanceof m.Day ? element.Value.toISOString() : element.Value.toString(),
			};
		}
		return undefined;
	};

	const getMultiMatches = (field: OcrMatchName) => {
		const showNumberOfBoxOptions = 5;

		const allFieldMatches = () => {
			return safeExhaustiveSwitch(
				() => {
					switch (field) {
						case "amount":
							return prediction.TotalSums;
						case "date":
							return prediction.Dates;
						case "paymentDate":
							return prediction.PaymentDates;
						case "paymentReference":
							return prediction.PaymentReferences;
						default:
							exhaustiveCheck(field);
					}
				},
				error => {
					trackTrace(error.message, undefined, "error");
					window.location.reload();

					return undefined; // To satisfy the return type
				},
			);
		};

		const fieldMatches: OcrMatch[] = [];
		const hideFields: OcrMatchName[] = [];
		allFieldMatches()
			?.slice(0, showNumberOfBoxOptions)
			.forEach((element: Match, index: number) => {
				const fieldIsSelected = selectedPredictions[field]?.index === index;
				const stroke = fieldIsSelected ? ocrColors[field] : ocrColors.default;
				const match = getMatchFromPossibleMatch(element, field, { ...stroke, fill: "#fff", fillOpacity: 0.2 }, false);
				if (match) {
					fieldMatches.push(match);
				} else {
					hideFields.push(field);
				}
			});

		return { fieldMatches, hideFields };
	};

	const getSingleMatches = () => {
		const fieldMatches: OcrMatch[] = [];
		const hideFields: OcrMatchName[] = [];
		const totalSum = prediction.UserEnteredSum ? undefined : bestMatch(prediction.TotalSums);
		const date = bestMatch(prediction.Dates);
		const paymentDate = prediction.UserEnteredPaymentDate ? undefined : bestMatch(prediction.PaymentDates);
		const fill = { fill: "#fff", fillOpacity: 0.2 };
		if (totalSum) {
			const match = getMatchFromPossibleMatch(totalSum, "amount", { ...fill, ...ocrColors.amount }, true);

			if (match && !hideTypes.includes("amount")) {
				fieldMatches.push(match);
			} else {
				hideFields.push("amount");
			}
		}

		if (date) {
			const match = getMatchFromPossibleMatch(date, "date", { ...fill, ...ocrColors.date }, true);

			if (match && !hideTypes.includes("date")) {
				fieldMatches.push(match);
			} else {
				hideFields.push("date");
			}
		}

		if (paymentDate) {
			const match = getMatchFromPossibleMatch(paymentDate, "paymentDate", { ...fill, ...ocrColors.paymentDate }, true);

			if (match && !hideTypes.includes("paymentDate")) {
				fieldMatches.push(match);
			} else {
				hideFields.push("paymentDate");
			}
		}

		// We don't support payment reference for single matches atm

		return { fieldMatches, hideFields };
	};

	return !multiPrediction
		? getSingleMatches()
		: showType
			? getMultiMatches(showType)
			: { fieldMatches: [], hideFields: [] };
};
